import { notify } from '@/components/composed/notify';
import { ConfirmDialog } from '@/components/composed/ConfirmDialog';
import { ApiError } from '@/lib/api/errors';
import type { Entity, EntityRelationship } from '@/lib/api/domains/entities';
import { useEndRelationship } from '../api/entity-mutations';

/*
 * End-a-relationship confirmation (E02.F04 R4, AC-6fb9640d). Before a
 * relationship is ended, this shows the operator WHAT ENDING IT WILL STOP — the
 * two parties and the kind — so the consequence is named before it is
 * committed. Abandon ends nothing; confirm sends the end to the server, which
 * records who ended it and when (AC-657864f0, the server's concern).
 *
 * The frontend holds no authorization: whether this caller may end the
 * relationship, and the SR-28 cancel-vs-end rule, are the server's (design §5).
 * The end date sent is today's business date is NOT computed here — the server
 * owns the business date; this confirm sends the operator's chosen end date.
 */
export function EndRelationshipConfirm({
  entity,
  relationship,
  endDate,
  open,
  onOpenChange,
}: {
  entity: Entity;
  relationship: EntityRelationship;
  endDate: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const endRelationship = useEndRelationship(entity.id);

  async function onConfirm() {
    try {
      await endRelationship.mutateAsync({
        relationshipId: relationship.id,
        body: { endDate },
      });
      notify.success('Relationship ended.');
    } catch (error) {
      notify.error(
        error instanceof ApiError
          ? error.message
          : 'Failed to end the relationship.',
      );
      throw error;
    }
  }

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="End this relationship?"
      description={
        <span>
          Ending this will stop the <strong>{relationship.kind}</strong>{' '}
          relationship between <strong>{relationship.fromName}</strong> and{' '}
          <strong>{relationship.toName}</strong>. Kelly will no longer service,
          bill or pay against it.
        </span>
      }
      confirmLabel="End relationship"
      cancelLabel="Abandon"
      destructive
      onConfirm={onConfirm}
    />
  );
}
